import { useEffect } from 'react';

// Mounted only inside the editor iframe (edit mode). Highlights editable strings
// on hover (dashed outline + a small floating pencil badge, so it's obvious
// text is clickable before you click it), posts the clicked key up to the
// parent editor window, and can scroll+flash a specific key on request (used
// by the parent's page/section chips to jump to copy that's off-screen).
export function CopyEditBridge() {
  useEffect(() => {
    const findKeyEl = (el: EventTarget | null): HTMLElement | null =>
      el instanceof HTMLElement ? el.closest('[data-copy-key]') : null;

    const badge = document.createElement('div');
    badge.textContent = '✎';
    Object.assign(badge.style, {
      position: 'fixed',
      zIndex: '2147483647',
      width: '18px',
      height: '18px',
      borderRadius: '9999px',
      background: '#171717',
      color: '#fff',
      fontSize: '10px',
      lineHeight: '18px',
      textAlign: 'center',
      pointerEvents: 'none',
      display: 'none',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(badge);

    const showBadgeFor = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      badge.style.display = 'block';
      badge.style.top = `${rect.top - 9}px`;
      badge.style.left = `${rect.right - 9}px`;
    };

    const onOver = (e: MouseEvent) => {
      const el = findKeyEl(e.target);
      if (!el) return;
      el.style.outline = '1.5px dashed #737373';
      el.style.outlineOffset = '3px';
      el.style.cursor = 'pointer';
      showBadgeFor(el);
    };
    const onOut = (e: MouseEvent) => {
      const el = findKeyEl(e.target);
      if (el) {
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.cursor = '';
      }
      badge.style.display = 'none';
    };
    const onClick = (e: MouseEvent) => {
      const el = findKeyEl(e.target);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const key = el.getAttribute('data-copy-key');
      if (key) window.parent.postMessage({ source: 'copy-editor', type: 'select', key }, window.location.origin);
    };

    const flash = (el: HTMLElement) => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.transition = 'outline-color 0.15s, background-color 0.15s';
      el.style.outline = '2px solid #d97706';
      el.style.outlineOffset = '3px';
      el.style.backgroundColor = 'rgba(217, 119, 6, 0.12)';
      setTimeout(() => {
        el.style.outline = '';
        el.style.outlineOffset = '';
        el.style.backgroundColor = '';
      }, 900);
    };

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.source !== 'copy-editor') return;
      if (e.data.type === 'scrollToKey') {
        const el = document.querySelector<HTMLElement>(`[data-copy-key="${e.data.key}"]`);
        if (el) flash(el);
      } else if (e.data.type === 'scrollToSection') {
        const el = document.querySelector<HTMLElement>(`[data-copy-section="${e.data.section}"]`);
        if (el) flash(el);
      }
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('click', onClick, true);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('message', onMessage);
      badge.remove();
    };
  }, []);

  return null;
}
